import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogTitle,
	IconButton,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	useMediaQuery,
	useTheme,
} from '@material-ui/core';
import ArrowRightIcon from '../../icons/ArrowRight';
import PencilAltIcon from '../../icons/PencilAlt';
import SearchIcon from '../../icons/Search';
import Scrollbar from '../Scrollbar';
import { PaginationResult } from '../../types/paginationResult';
import { useTranslation } from 'react-i18next';
import Tooltip from '@material-ui/core/Tooltip';
import { User } from '../../models/User';
import TableRowsLoader from '../TableRowsLoader';
import { DeleteOutline } from '@material-ui/icons';
import { UserService } from '../../services/UserService';
import { composeNotistackMessage } from '../../utils/common';

interface UsersListTableProps {
	paginationResult: PaginationResult<User> | undefined;
	getUsers(currentPage: number, recordLimit: number, searchFilter: string): void;
	isPageLoading: boolean;
	loggedUser: any;
}

const UsersListTable: FC<UsersListTableProps> = ({ paginationResult, getUsers, isPageLoading, loggedUser }) => {
	const { t } = useTranslation();

	const [currentPage, setCurrentPage] = useState<number>(0);
	const [recordLimit, setRecordLimit] = useState<number>(10);
	const [searchFilter, setSearchFilter] = useState<string>('');

	const [userIdToRemove, setUserIdToRemove] = useState<string | undefined>('');
	const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
	const handleUserModalOpen = () => setRemoveUserModalOpen(true);
	const handleUserModalClose = () => setRemoveUserModalOpen(false);
	var currentTheme = useTheme();
	const mobileDevice = useMediaQuery(currentTheme.breakpoints.down(450));

	useEffect(() => {
		getUsers(currentPage + 1, recordLimit, searchFilter);
	}, [currentPage, recordLimit, searchFilter, getUsers]);

	const deleteUser = async () => {
		if (userIdToRemove !== undefined) {
			const userService = new UserService();

			let deleteUserResult = await userService.deleteTenantUserById(userIdToRemove, loggedUser?.currentTenantId ?? '');

			setUserIdToRemove('');
			getUsers(currentPage + 1, recordLimit, searchFilter);

			if (deleteUserResult) {
				composeNotistackMessage(t('USER_DELETED'), 'success');
			} else {
				composeNotistackMessage(t('USER_NOT_DELETED'), 'error');
			}
		}
	}

	return (
		<Card>
			<Box
				sx={{
					alignItems: 'center',
					display: 'flex',
					flexWrap: 'wrap',
					m: -1,
					p: 2
				}}
			>
				<Box
					sx={{
						m: 1,
						maxWidth: '100%',
						width: 500
					}}
				>
					<TextField
						fullWidth
						size='small'
						sx={{
							input: {
								color: 'white'
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							)
						}}
						onChange={(e) => {
							setSearchFilter(e.target.value);
						}}
						placeholder={t('SEARCH_USERS')}
						value={searchFilter}
						variant="outlined"
					/>
				</Box>
			</Box>
			<Scrollbar>
				<Box sx={{ minWidth: 700 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>
									{t('EMAIL')}
								</TableCell>
								<TableCell>
									{t('FIRST_NAME')}
								</TableCell>
								<TableCell>
									{t('SURNAME')}
								</TableCell>
								<TableCell>
									{t('ROLE')}
								</TableCell>
								<TableCell align="right">
									{t('ACTIONS')}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{
								isPageLoading ?
									(
										<TableRowsLoader rowsNum={recordLimit} colsNum={5} />
									) :
									(<>
										{paginationResult?.items.map((user) => (
											<TableRow hover key={user.id}>
												<TableCell>
													<Box
														sx={{
															alignItems: 'center',
															display: 'flex'
														}}
													>
														<Box sx={{ ml: 1 }}>
															{user.email}
														</Box>
													</Box>
												</TableCell>
												<TableCell>
													{user.name}
												</TableCell>
												<TableCell>
													{user.surname}
												</TableCell>
												<TableCell>
													{t(user.tenantUser.role.name)}
												</TableCell>
												<TableCell align="right">
													<Dialog
														open={removeUserModalOpen}
														onClose={handleUserModalClose}
														aria-labelledby="alert-remove-user-title"
														aria-describedby="alert-remove-user-description"
													>
														<DialogTitle id="remove-user-title">
															{t('TITLE_MODAL_DELETE_USER')}
														</DialogTitle>
														<DialogActions>
															<Button onClick={handleUserModalClose} variant="outlined" color="error">{t('CANCEL')}</Button>
															<Button onClick={() => {
																handleUserModalClose();
																deleteUser();
															}} variant="contained" color="error">{t('DELETE')}</Button>
														</DialogActions>
													</Dialog>
													<Tooltip title={t('DELETE') || ''} aria-label={t('DELETE') || ''}>
														<IconButton onClick={() => {
															handleUserModalOpen();
															setUserIdToRemove(user.id)
														}}>
															<DeleteOutline fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title={t('EDIT') || ''} aria-label={t('EDIT') || ''}>
														<IconButton
															component={RouterLink}
															to={`edit?userId=${user.id}`}
														>
															<PencilAltIcon fontSize="small" />
														</IconButton>
													</Tooltip>

													<Tooltip title={t('DETAILS') || ''} aria-label={t('DETAILS') || ''}>
														<IconButton
															component={RouterLink}
															to={`detail?userId=${user.id}`}
														>
															<ArrowRightIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										))}
									</>)
							}
						</TableBody>
					</Table>
				</Box>
			</Scrollbar>
			<TablePagination
				component="div"
				count={paginationResult?.totalCount ?? 10}
				onRowsPerPageChange={(e) => {
					setRecordLimit(parseInt(e.target.value, 10));
				}}
				onPageChange={(e, page) => {
					setCurrentPage(page);
				}}
				page={currentPage}
				rowsPerPage={recordLimit}
				rowsPerPageOptions={[5, 10, 25]}
				labelRowsPerPage={!mobileDevice && t('ROWS_PER_PAGE')}
			/>
		</Card>
	);
};

export default UsersListTable;
